import axios from 'axios'
import store from '@/store'
import { getAccessToken } from '@/utils/auth'
import { Logger } from '@/utils/logger'

const log = Logger.create('NexusSeatKeepAlive')

/** Match Nexus docs: heartbeat every ~2 minutes (active window is 5 minutes). */
export const NEXUS_SEAT_HEARTBEAT_MS = 2 * 60 * 1000
export const NEXUS_SEAT_MAX_CONSECUTIVE_FAILURES = 3
export const NEXUS_SEAT_FAILURE_EVENT = 'nexus-seat-keepalive-failed'

const HEARTBEAT_PATH = '/auth/sso/tubtim/heartbeat'
const LOGOUT_PATH = '/auth/sso/tubtim/logout'

let intervalId = null
let inFlight = false
let consecutiveFailures = 0

function apiBaseUrl () {
  return process.env.VUE_APP_API_URL || ''
}

function emitFailure (reason, message) {
  try {
    window.dispatchEvent(new CustomEvent(NEXUS_SEAT_FAILURE_EVENT, {
      detail: { reason, message }
    }))
  } catch (err) {
    log.debug('failed to emit failure event', err?.message || err)
  }
}

/**
 * Raw axios call — avoid HttpRequest loading spinner on every heartbeat.
 * @param {string} path
 */
async function authPost (path) {
  const base = apiBaseUrl()
  const token = getAccessToken()
  if (!base || !token) {
    return { skipped: true }
  }

  return axios.post(`${base}${path}`, {}, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    timeout: 15000,
    validateStatus: (status) => (status >= 200 && status < 300) || status === 204
  })
}

export async function sendNexusSeatHeartbeat () {
  if (inFlight) return
  if (!store.getters['User/isTubtimSso']) return
  if (!getAccessToken()) return

  inFlight = true
  try {
    await authPost(HEARTBEAT_PATH)
    consecutiveFailures = 0
  } catch (err) {
    consecutiveFailures += 1
    log.debug('heartbeat failed', err?.message || err, consecutiveFailures)

    if (consecutiveFailures >= NEXUS_SEAT_MAX_CONSECUTIVE_FAILURES) {
      stopNexusSeatKeepAlive()
      emitFailure(
        'heartbeat',
        'Could not keep your Nexus seat active. Please sign in again if licensing fails.'
      )
      consecutiveFailures = 0
    }
  } finally {
    inFlight = false
  }
}

export function startNexusSeatKeepAlive () {
  stopNexusSeatKeepAlive()
  consecutiveFailures = 0
  if (!store.getters['User/isTubtimSso']) return

  sendNexusSeatHeartbeat()
  intervalId = setInterval(sendNexusSeatHeartbeat, NEXUS_SEAT_HEARTBEAT_MS)
}

export function stopNexusSeatKeepAlive () {
  if (intervalId) {
    clearInterval(intervalId)
    intervalId = null
  }
}

/**
 * Best-effort release before clearing local session.
 */
export async function releaseNexusSeatKeepAlive () {
  stopNexusSeatKeepAlive()
  consecutiveFailures = 0
  if (!store.getters['User/isTubtimSso']) return
  try {
    await authPost(LOGOUT_PATH)
  } catch (err) {
    log.debug('logout release failed', err?.message || err)
  }
}

export default {
  NEXUS_SEAT_HEARTBEAT_MS,
  NEXUS_SEAT_MAX_CONSECUTIVE_FAILURES,
  NEXUS_SEAT_FAILURE_EVENT,
  startNexusSeatKeepAlive,
  stopNexusSeatKeepAlive,
  releaseNexusSeatKeepAlive,
  sendNexusSeatHeartbeat
}
